import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators';

import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { detectMobileViewport } from '../helpers/device';
import featuresInfo from '../helpers/state/features-info';
import { getEmbedCode } from '../helpers/api/topics-api';
import { logWithAmplitude } from '../helpers/api/api';
import topicData from '../helpers/state/topic-data';
import { getMainPage } from '../helpers/page-name';

@customElement('social-share-buttons')
export class SocialShareButtons extends LitElement {
    static amplitude_callsite: string = 'Share Map Component';

    @property({ type: String })
    shareLink: string = '';

    @property({ type: String })
    printableLink: string | undefined;

    @property({ type: String })
    pageName: string = '';

    @property({ type: String })
    size: string = 'lg';

    @property({ type: Boolean })
    buttonsInColumn: boolean = false;

    @property({ type: String })
    qrCodeLink: string = '';

    @property({ type: Boolean })
    scrollable: boolean = false;

    @property({ type: Boolean })
    hideQrCodeButton: boolean = false;

    @property({ type: Boolean })
    hidePrintButton: boolean = false;

    @property({ type: Boolean })
    hideEmbedButton: boolean = false;

    @property({ type: Boolean })
    hideLinkButton: boolean = false;

    @property({ type: Boolean })
    isTopicWriter: boolean = false;

    private readonly bindAccountState = new StateController(this, accountInfo);

    connectedCallback() {
        super.connectedCallback();
        if (this.size === 'lg') {
            this.style.setProperty('--btn-size', '70px');
            this.style.setProperty('--icon-size', '30px');

        } else if (this.size === 'md') {
            this.style.setProperty('--btn-size', '32px');
            this.style.setProperty('--icon-size', '18px');
        } else if (this.size === 'sm') {
            this.style.setProperty('--btn-size', '40px');
            this.style.setProperty('--icon-size', '20px');
        }
    }

    render() {
        return html`
            <div class="share-buttons-wrapper ${this.scrollable ? 'share-buttons-wrapper--scrollable' : ''} ${this.buttonsInColumn ? 'share-buttons-wrapper--vertical' : 'share-buttons-wrapper--horizontal'}">
                ${this.hideLinkButton ? '' : html`
                    <div>
                        <mwc-icon-button @click="${this.linkClick}" icon="link"></mwc-icon-button>
                        <p class="${this.buttonsInColumn ? 'hidden' : ''}">Map Link</p>
                    </div>
                `}
                ${this.hideEmbedButton ? '' : html`
                    <div>
                        <mwc-icon-button @click="${this.embedClick}" icon="code"></mwc-icon-button>
                        <p class="${this.buttonsInColumn ? 'hidden' : ''}">Embed</p>
                    </div>
                `}
                ${!this.hideQrCodeButton ? html`
                    <div>
                        <mwc-icon-button @click="${this.qrCode}" icon="qr_code"></mwc-icon-button>
                        <p class="${this.buttonsInColumn ? 'hidden' : ''}">QR Code</p>
                    </div>
                ` : ''}
                ${this.buttonsInColumn ? '' : html`
                    <div>
                        <mwc-icon-button @click="${this.emailClick}">
                            <img src="/images/socials/mail.svg" alt="Email icon" />
                        </mwc-icon-button>
                        <p class="${this.buttonsInColumn ? 'hidden' : ''}">Email</p>
                    </div>
                `}
                ${(detectMobileViewport() || this.printableLink === undefined || this.hidePrintButton || this.buttonsInColumn) ? '' :
                    featuresInfo.printableMapEnabled ? html`
                        <div>
                            <mwc-icon-button @click="${this.print}" icon="print"></mwc-icon-button>
                            <p class="${this.buttonsInColumn ? 'hidden' : ''}">Print</p>
                        </div>
                    ` : html`
                        <div>
                            <mwc-icon-button @click="${this.print}">
                                <img src="/images/generic_upgrade_small.png"/>
                            </mwc-icon-button>
                            <p class="${this.buttonsInColumn ? 'hidden' : ''}">Print</p>
                        </div>
                `}
                <div>
                    <mwc-icon-button @click="${this.text}">
                        <img src="/images/socials/comment.png" alt="Messaging icon" />
                    </mwc-icon-button>
                    <p class="${this.buttonsInColumn ? 'hidden' : ''}">Text</p>
                </div>
                <!-- <div>
                    <mwc-icon-button @click="}">
                        <img src="/images/socials/twitter.png" alt="Twitter icon" />
                    </mwc-icon-button>
                    <p class="">Twitter</p>
                </div> -->
                <div>
                    <mwc-icon-button @click="${this.facebook}">
                        <img src="/images/socials/facebook.png" alt="Facebook icon" />
                    </mwc-icon-button>
                    <p class="${this.buttonsInColumn ? 'hidden' : ''}">Facebook</p>
                </div>
                <div>
                    <mwc-icon-button @click="${this.reddit}">
                        <img src="/images/socials/reddit.png" alt="Reddit icon" />
                    </mwc-icon-button>
                    <p class="${this.buttonsInColumn ? 'hidden' : ''}">Reddit</p>
                </div>
                <div>
                    <mwc-icon-button @click="${this.linkedin}">
                        <img src="/images/socials/linkedin.png" alt="Linkedin icon" />
                    </mwc-icon-button>
                    <p class="${this.buttonsInColumn ? 'hidden' : ''}">LinkedIn</p>
                </div>
                <div>
                    <mwc-icon-button @click="${this.pinterest}">
                        <img src="/images/socials/pinterest.png" alt="Pinterest icon" />
                    </mwc-icon-button>
                    <p class="${this.buttonsInColumn ? 'hidden' : ''}">Pinterest</p>
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    private async qrCode() {
        this.sendSocialMediaTelemetry('QR code');
        this.dispatchEvent(
            new CustomEvent('qr-code-click')
        );

        if (this.buttonsInColumn) {
            window.open(this.qrCodeLink);
        }
    }

    private twitter() {
        this.sendSocialMediaTelemetry('twitter');
        window.open(`https://twitter.com/share?url=${this.shareLink}`);
    }

    private linkedin() {
        this.sendSocialMediaTelemetry('linkedin');
        window.open(`https://www.linkedin.com/shareArticle?url=${this.shareLink}`);
    }

    private reddit() {
        this.sendSocialMediaTelemetry('reddit');
        window.open(`https://reddit.com/submit?url=${this.shareLink}`);
    }

    private pinterest() {
        this.sendSocialMediaTelemetry('pinterest');
        window.open(`https://pinterest.com/pin/create/bookmarklet/?url=${this.shareLink}`);
    }

    private facebook() {
        this.sendSocialMediaTelemetry('facebook');
        window.open(`https://www.facebook.com/sharer.php?u=${this.shareLink}`);
    }

    private email() {
        this.sendSocialMediaTelemetry('email');
        const link = `mailto:?&subject=Check out this map!&body=${this.shareLink}`;
        window.location.href = link;
    }

    private text() {
        this.sendSocialMediaTelemetry('text');
        const link = 'sms:?' + '&body=' + this.shareLink;
        window.location.href = link;
    }

    private embedClick() {
        this.sendSocialMediaTelemetry('embed');
        this.dispatchEvent(
            new CustomEvent('embed-click')
        );

        if (this.buttonsInColumn) {
            const embedCode = getEmbedCode(SocialShareButtons.amplitude_callsite, this.shareLink, false);
            // copy to clipboard
            const el = document.createElement('textarea');
            el.value = embedCode;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);

            getMainPage().errorToastElement.showToast('Embed code copied to clipboard');
        }
    }

    private emailClick() {
        this.sendSocialMediaTelemetry('email');
        this.dispatchEvent(
            new CustomEvent('email-click')
        );
    }

    private linkClick() {
        this.sendSocialMediaTelemetry('link');
        this.dispatchEvent(
            new CustomEvent('link-click')
        );

        if (this.buttonsInColumn) {
            // copy to clipboard
            const el = document.createElement('textarea');
            el.value = this.shareLink;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);

            getMainPage().errorToastElement.showToast('Link copied to clipboard');
        }
    }

    private sendSocialMediaTelemetry(label: string) {
        gtag('event', 'click', {
            event_category: 'social media clicks',
            event_label: label,
        });
        logWithAmplitude(
            SocialShareButtons.amplitude_callsite,
            `Share Map Click`, {
                topic_id: topicData.topicId,
                social_click: label,
                click_location: window.location.hash.split('?')[0]
            }
        );
    }

    private async print() {
        if (featuresInfo.printableMapEnabled) {
            const w = window.open(this.shareLink);
            w?.location.replace(this.printableLink ?? '');
        } else {
            logWithAmplitude(SocialShareButtons.amplitude_callsite, 'Upgrade Dialog Opened');
            this.dispatchEvent(
                new CustomEvent('open-upgrade-dialog', {
                    detail: {
                        type: 'open-upgrade-dialog',
                    },
                })
            );
        }
    }

    static get styles() {
        return css`
            :host {
                display: block;
            }

            mwc-icon-button {
                --mdc-icon-button-size: var(--btn-size);
                --mdc-icon-size: var(--icon-size);
                color: var(--theme-input-color);
                border-radius: 100px;
                margin: 0 6px;
                border: 1px solid var(--main-moon-color);
            }

            img {
                width: auto;
                height: var(--icon-size);
            }

            .share-buttons-wrapper {
                margin-top: 0.5rem;
                padding-bottom: 0.5rem;
                display: flex;
            }

            .share-buttons-wrapper div {
                display: flex;
                flex-direction: column;
                align-items: center;
                margin: 0 5px;
            }

            .share-buttons-wrapper div p {
                margin: 0;
                font-size: .8rem;
                text-align: center;
            }

            .share-buttons-wrapper--vertical {
                flex-direction: column;
                gap: 0.5rem;
            }

            .share-buttons-wrapper--scrollable {
                flex-wrap: nowrap;
                overflow-x: auto;
            }

            .share-buttons-wrapper::-webkit-scrollbar {
                width: 5px;
            }

            .share-buttons-wrapper::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .share-buttons-wrapper::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }

            .qrCodeImg {
                width: 150px;
                height: auto;
            }

            .hidden {
                display: none;
            }
        `;
    }
}
